import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import api from '../services/api';

interface VisualizationData {
  client_name: string;
  total_nodes: number;
  total_edges: number;
  avg_links_per_page: number;
  categories: {
    [key: string]: {
      count: number;
      color: string;
      name: string;
    };
  };
  link_types: {
    [key: string]: {
      count: number;
      color: string;
    };
  };
  edge_traces: {
    [key: string]: {
      x: (number | null)[];
      y: (number | null)[];
      z: (number | null)[];
      color: string;
      name: string;
    };
  };
  node_traces: {
    [key: string]: {
      x: number[];
      y: number[];
      z: number[];
      text: string[];
      size: number[];
      urls: string[];
      color: string;
      name: string;
    };
  };
}

interface TopicalMap3DVisualizationProps {
  jobId?: number;   // V1 mode
  mapId?: number;   // V2 mode
  onClose?: () => void;
  inline?: boolean; // Render inline (no modal)
}

const TopicalMap3DVisualization: React.FC<TopicalMap3DVisualizationProps> = ({
  jobId,
  mapId,
  onClose,
  inline = false
}) => {
  const [data, setData] = useState<VisualizationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadVisualizationData();
  }, [jobId, mapId]);

  const loadVisualizationData = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = mapId
        ? `/topicalmapv2/visualization/${mapId}`
        : `/topicalmap/${jobId}/visualization`;
      const response = await api.get(url);
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load visualization data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    if (inline) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-center mt-4 text-gray-600">Loading 3D visualization...</p>
          </div>
        </div>
      );
    }
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
          <p className="text-center mt-4 text-gray-600">Loading 3D visualization...</p>
        </div>
      </div>
    );
  }

  if (error) {
    if (inline) {
      return (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="text-red-600">
            <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="font-medium">Error loading visualization</p>
            <p className="text-sm mt-2">{error}</p>
          </div>
        </div>
      );
    }
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <div className="text-red-600 text-center">
            <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="font-medium">Error loading visualization</p>
            <p className="text-sm mt-2">{error}</p>
          </div>
          <button
            onClick={onClose}
            className="mt-4 w-full px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Build Plotly traces
  const traces: Plotly.Data[] = [];

  // Add edge traces
  Object.entries(data.edge_traces).forEach(([linkType, edgeData]) => {
    traces.push({
      type: 'scatter3d',
      mode: 'lines',
      x: edgeData.x,
      y: edgeData.y,
      z: edgeData.z,
      name: edgeData.name,
      line: {
        color: edgeData.color,
        width: linkType === 'parent' ? 2 : 1
      },
      opacity: linkType === 'sibling' ? 0.4 : 0.6,
      hoverinfo: 'skip'
    });
  });

  // Add node traces
  Object.entries(data.node_traces).forEach(([_category, nodeData]) => {
    traces.push({
      type: 'scatter3d',
      mode: 'markers',
      x: nodeData.x,
      y: nodeData.y,
      z: nodeData.z,
      name: nodeData.name,
      marker: {
        size: nodeData.size,
        color: nodeData.color,
        opacity: 0.9,
        line: {
          color: 'white',
          width: 0.5
        }
      },
      text: nodeData.text,
      hoverinfo: 'text',
      customdata: nodeData.urls
    });
  });

  const layout: Partial<Plotly.Layout> = {
    title: {
      text: `<b>3D Internal Linking Visualization</b><br><sup>${data.client_name} - ${data.total_nodes} Topics</sup>`,
      x: 0.5,
      font: { size: 18 }
    },
    scene: {
      xaxis: {
        showgrid: true,
        gridcolor: 'rgba(128,128,128,0.2)',
        showticklabels: false,
        title: { text: '' },
        backgroundcolor: 'rgba(0,0,0,0)'
      },
      yaxis: {
        showgrid: true,
        gridcolor: 'rgba(128,128,128,0.2)',
        showticklabels: false,
        title: { text: '' },
        backgroundcolor: 'rgba(0,0,0,0)'
      },
      zaxis: {
        showgrid: true,
        gridcolor: 'rgba(128,128,128,0.2)',
        showticklabels: false,
        title: { text: '' },
        backgroundcolor: 'rgba(0,0,0,0)'
      },
      bgcolor: 'rgba(240,240,245,1)',
      camera: {
        eye: { x: 1.5, y: 1.5, z: 1.2 }
      }
    },
    showlegend: true,
    legend: {
      x: 0.02,
      y: 0.98,
      bgcolor: 'rgba(255,255,255,0.9)',
      bordercolor: 'rgba(0,0,0,0.1)',
      borderwidth: 1,
      font: { size: 11 }
    },
    margin: { l: 0, r: 0, t: 60, b: 0 },
    paper_bgcolor: 'white',
    height: inline ? 600 : 700,
    autosize: true
  };

  // Inline rendering — no modal overlay
  if (inline) {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-6 py-4 border-b">
            <h2 className="text-xl font-bold text-gray-800">
              3D Internal Linking Visualization
            </h2>
            <p className="text-sm text-gray-500">
              {data.client_name} | {data.total_nodes} Topics | {data.total_edges} Links
            </p>
          </div>

          {/* Stats Bar */}
          <div className="px-6 py-3 bg-gray-50 border-b flex items-center gap-8">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-blue-600">{data.total_nodes}</span>
              <span className="text-sm text-gray-600">Topics</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-green-600">{data.total_edges}</span>
              <span className="text-sm text-gray-600">Links</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-amber-600">{data.avg_links_per_page}</span>
              <span className="text-sm text-gray-600">Avg Links/Page</span>
            </div>
          </div>

          {/* Categories and Link Types */}
          <div className="px-6 py-3 border-b flex items-center gap-6 flex-wrap">
            <span className="text-sm font-medium text-gray-700">Categories:</span>
            {Object.entries(data.categories).map(([key, cat]) => (
              <div key={key} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: cat.color }}
                ></div>
                <span className="text-sm text-gray-600">{cat.name} ({cat.count})</span>
              </div>
            ))}
            <span className="ml-4 text-sm font-medium text-gray-700">Links:</span>
            {Object.entries(data.link_types).map(([key, lt]) => (
              <div key={key} className="flex items-center gap-2">
                <div
                  className="w-4 h-0.5"
                  style={{ backgroundColor: lt.color }}
                ></div>
                <span className="text-sm text-gray-600 capitalize">{key} ({lt.count})</span>
              </div>
            ))}
          </div>

          {/* Visualization */}
          <div className="p-4">
            <Plot
              data={traces}
              layout={layout}
              config={{
                displayModeBar: true,
                displaylogo: false,
                modeBarButtonsToRemove: ['toImage'],
                responsive: true
              }}
              style={{ width: '100%', height: '100%' }}
              useResizeHandler={true}
            />
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t bg-gray-50 text-center text-xs text-gray-500">
            Generated by BNSystem TopicalMap Generator | Koray's Semantic SEO Methodology
          </div>
        </div>
      </div>
    );
  }

  // Modal rendering (default)
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-[95vw] h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              3D Internal Linking Visualization
            </h2>
            <p className="text-sm text-gray-500">
              {data.client_name} | {data.total_nodes} Topics | {data.total_edges} Links
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Stats Bar */}
        <div className="px-6 py-3 bg-gray-50 border-b flex items-center gap-8">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-blue-600">{data.total_nodes}</span>
            <span className="text-sm text-gray-600">Topics</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-green-600">{data.total_edges}</span>
            <span className="text-sm text-gray-600">Links</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-amber-600">{data.avg_links_per_page}</span>
            <span className="text-sm text-gray-600">Avg Links/Page</span>
          </div>
          <div className="flex-1"></div>
          <div className="text-xs text-gray-500 flex items-center gap-4">
            <span>🖱️ <b>Rotate:</b> Click & drag</span>
            <span>🔍 <b>Zoom:</b> Scroll</span>
            <span>📍 <b>Pan:</b> Right-click</span>
            <span>ℹ️ <b>Details:</b> Hover</span>
          </div>
        </div>

        {/* Categories and Link Types */}
        <div className="px-6 py-3 border-b flex items-center gap-6 flex-wrap">
          <span className="text-sm font-medium text-gray-700">Categories:</span>
          {Object.entries(data.categories).map(([key, cat]) => (
            <div key={key} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: cat.color }}
              ></div>
              <span className="text-sm text-gray-600">{cat.name} ({cat.count})</span>
            </div>
          ))}
          <span className="ml-4 text-sm font-medium text-gray-700">Links:</span>
          {Object.entries(data.link_types).map(([key, lt]) => (
            <div key={key} className="flex items-center gap-2">
              <div
                className="w-4 h-0.5"
                style={{ backgroundColor: lt.color }}
              ></div>
              <span className="text-sm text-gray-600 capitalize">{key} ({lt.count})</span>
            </div>
          ))}
        </div>

        {/* Visualization */}
        <div className="flex-1 p-4">
          <Plot
            data={traces}
            layout={layout}
            config={{
              displayModeBar: true,
              displaylogo: false,
              modeBarButtonsToRemove: ['toImage'],
              responsive: true
            }}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler={true}
          />
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-gray-50 text-center text-xs text-gray-500">
          Generated by BNSystem TopicalMap Generator | Koray's Semantic SEO Methodology
        </div>
      </div>
    </div>
  );
};

export default TopicalMap3DVisualization;
